// src/components/Control/ControlPanel/AutonomyPanel.tsx
// «Настройки → Автономия» (mission_node на Pi): объезд препятствий, человек,
// приветствие. Режимы запускаются из меню режима в шапке, речь робота на
// сайте проигрывает система событий страницы.
//   robot/<id>/config   <- {"autonomy": {...}, "person": {...}} (сливается на роботе)
//   robot/<id>/status   -> "settings" (текущие значения, через общую телеметрию)
import React, { useState } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { useTelemetryState } from '../robot/RobotContext';

interface AutonomyCfg {
  sources: { lidar: boolean; camera: boolean };
  clearance: number;
  speed: number;
  wall: 'right' | 'left';
  explore?: 'map' | 'wall';
}

interface PersonCfg {
  detect: boolean;
  auto_approach: boolean;
  auto_follow: boolean;
  target_fill: number;
  greet_text: string;
  audio: 'robot' | 'site' | 'both' | 'off';
  gesture_stop: boolean;
  greet_repeat_min: number;
}

const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0', fontSize: 13, flexWrap: 'wrap' };
const label: React.CSSProperties = { flex: '0 0 190px', color: '#444' };

const AutonomyPanel: React.FC<{ zenohSession: zenoh.Session | null; robotId: string }> = ({ zenohSession, robotId }) => {
  const autonomy = useTelemetryState(t => (t.status?.settings?.autonomy ?? null) as AutonomyCfg | null);
  const person = useTelemetryState(t => (t.status?.settings?.person ?? null) as unknown as PersonCfg | null);
  const [greet, setGreet] = useState<string | null>(null);  // text being edited

  const send = (update: object) =>
    zenohSession?.put(`robot/${robotId}/config`, JSON.stringify(update)).catch(err => console.warn('config put failed', err));

  return (
    <div className="feature-card autonomy-panel">
      <h3><span>Автономия</span></h3>
      {!autonomy && !person && <p className="hint">Нет настроек от робота (стек на Pi не запущен?)</p>}
      {autonomy && (
        <>
          <h4 style={{ margin: '8px 0 4px' }}>Объезд препятствий</h4>
          <div style={row}>
            <span style={label}>Источники препятствий</span>
            <label><input type="checkbox" checked={autonomy.sources.lidar}
                          onChange={e => send({ autonomy: { sources: { lidar: e.target.checked } } })} /> лидар</label>
            <label><input type="checkbox" checked={autonomy.sources.camera}
                          onChange={e => send({ autonomy: { sources: { camera: e.target.checked } } })} /> камера 2</label>
          </div>
          <div style={row}>
            <span style={label}>Зазор до препятствий</span>
            <select value={autonomy.clearance} onChange={e => send({ autonomy: { clearance: Number(e.target.value) } })}>
              {[0.1, 0.15, 0.2, 0.3, 0.4].map(v => <option key={v} value={v}>{Math.round(v * 100)} см</option>)}
            </select>
            <span style={{ color: '#888', fontSize: 12 }}>от корпуса робота</span>
          </div>
          <div style={row}>
            <span style={label}>Объезд комнаты</span>
            <select value={autonomy.explore ?? 'map'} onChange={e => send({ autonomy: { explore: e.target.value } })}>
              <option value="map">по карте: все свободные места</option>
              <option value="wall">вдоль стены</option>
            </select>
            <span style={{ color: '#888', fontSize: 12 }}>по карте нужен SLAM; без карты — вдоль стены</span>
          </div>
          <div style={row}>
            <span style={label}>Скорость / стена</span>
            <select value={autonomy.speed} onChange={e => send({ autonomy: { speed: Number(e.target.value) } })}>
              {[0.08, 0.12, 0.18, 0.25].map(v => <option key={v} value={v}>{v} м/с</option>)}
            </select>
            <select value={autonomy.wall} onChange={e => send({ autonomy: { wall: e.target.value } })}>
              <option value="right">вдоль правой стены</option>
              <option value="left">вдоль левой стены</option>
            </select>
          </div>
        </>
      )}

      {person && (
        <>
          <h4 style={{ margin: '8px 0 4px' }}>Человек и приветствие</h4>
          <div style={row}>
            <span style={label}>Распознавание (камера 1)</span>
            <label><input type="checkbox" checked={person.detect}
                          onChange={e => send({ person: { detect: e.target.checked } })} /> включено</label>
          </div>
          <div style={row}>
            <span style={label}>Сам подъезжать к людям</span>
            <label title="В ожидании и при объезде: увидел человека — подъехать и поздороваться">
              <input type="checkbox" checked={person.auto_approach}
                     onChange={e => send({ person: { auto_approach: e.target.checked } })} /> да</label>
          </div>
          <div style={row}>
            <span style={label}>После приветствия</span>
            <label><input type="checkbox" checked={person.auto_follow}
                          onChange={e => send({ person: { auto_follow: e.target.checked } })} /> следовать за человеком</label>
          </div>
          <div style={row}>
            <span style={label}>Подъехать до</span>
            <select value={person.target_fill} onChange={e => send({ person: { target_fill: Number(e.target.value) } })}>
              {[0.15, 0.2, 0.3, 0.4, 0.5].map(v => <option key={v} value={v}>{Math.round(v * 100)}% кадра</option>)}
            </select>
          </div>
          <div style={row}>
            <span style={label}>Приветствие</span>
            <input type="text" value={greet ?? person.greet_text} maxLength={200} style={{ flex: 1, minWidth: 0 }}
                   onChange={e => setGreet(e.target.value)}
                   onBlur={() => { if (greet != null) { send({ person: { greet_text: greet } }); setGreet(null); } }} />
          </div>
          <div style={row}>
            <span style={label}>Не здороваться повторно</span>
            <select value={person.greet_repeat_min} onChange={e => send({ person: { greet_repeat_min: Number(e.target.value) } })}
                    title="С тем же человеком (узнанным по одежде) не чаще">
              <option value={0}>здороваться всегда</option>
              {[5, 10, 30, 60, 240].map(m => <option key={m} value={m}>{m < 60 ? `${m} мин` : `${m / 60} ч`}</option>)}
            </select>
          </div>
          <div style={row}>
            <span style={label}>Звук</span>
            <select value={person.audio} onChange={e => send({ person: { audio: e.target.value } })}>
              <option value="robot">динамик робота</option>
              <option value="site">на сайте</option>
              <option value="both">робот и сайт</option>
              <option value="off">выключен</option>
            </select>
          </div>
          <div style={row}>
            <span style={label}>Жест «стоп»</span>
            <label title="Обе руки над головой около секунды: остановить подъезд или следование">
              <input type="checkbox" checked={person.gesture_stop}
                     onChange={e => send({ person: { gesture_stop: e.target.checked } })} /> обе руки вверх</label>
          </div>
        </>
      )}
    </div>
  );
};

export default AutonomyPanel;
